import { MouseEvent, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAuth } from '../components/Providers/AuthProvider';
import { updateRelation } from '../redux/actions/userSpecificRelationsActions';
import { myFolder } from '../redux/consts';
import { selectFolders } from '../redux/reducers/folderReducer';
import { selectPrompts } from '../redux/reducers/promptReducer';
import { AppDispatch } from '../redux/store';
import { PromptView, PromptViewToUrlMap } from '../Types/enums';
import { Prompt } from '../Types/prompt';
import { fetchPromptsUtil } from '../util/fetchPromptsUtil';
import { useClassicNavigate } from './useClassicNavigate';

interface UsePinPromptProps {
  redirectOnPin?: boolean;
}
/**
 * Custom hook for handling prompt pin functionality
 * @param onPromptEdit Callback function to execute after pin action
 * @param setPinOptionsAnchorEl Optional state setter for pin options menu anchor
 * @returns Pin handling functions
 */
export function usePinPrompt({ redirectOnPin }: UsePinPromptProps) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const { currentUser } = useAuth();
  const { currentFolder } = useSelector(selectFolders);
  const { promptView } = useSelector(selectPrompts);
  const [pinOptionsAnchorEl, setPinOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [pinOptionsMenuOpen, setPinOptionsMenuOpen] = useState(false);

  // Function to change folder pin status
  const handleFolderPin = useCallback(
    async (prompt: Prompt) => {
      if (!currentUser || !currentFolder) return;

      if (currentFolder.id !== myFolder.id) {
        await dispatch(
          updateRelation({
            promptId: prompt.id,
            pinned: !prompt.folderPinned,
            folderId: currentFolder.id,
          })
        );
      }

      if (!prompt.folderPinned && promptView !== PromptView.PINNED && redirectOnPin) {
        const viewPath = PromptViewToUrlMap[PromptView.PINNED];
        navigate(`/folder/${currentFolder?.id}/prompts/${viewPath}`);
      }
      fetchPromptsUtil(dispatch, prompt.featured);
    },
    [currentUser, currentFolder, dispatch, promptView, redirectOnPin, navigate]
  );

  // Function to change personal pin status
  const handleUserPin = useCallback(
    async (prompt: Prompt, isPinned: boolean) => {
      await dispatch(
        updateRelation({
          promptId: prompt.id,
          pinned: !isPinned,
        })
      );

      if (!isPinned && promptView !== PromptView.MY_PINNED && redirectOnPin) {
        const viewPath = PromptViewToUrlMap[PromptView.MY_PINNED];
        navigate(`/folder/${currentFolder?.id}/prompts/${viewPath}`);
      }
      fetchPromptsUtil(dispatch, prompt.featured);
    },
    [dispatch, promptView, redirectOnPin, navigate, currentFolder]
  );

  // Combined function for UI interaction
  const handlePinMenuSetup = useCallback(
    (e: MouseEvent<HTMLElement>, prompt: Prompt) => {
      e.stopPropagation();
      e.preventDefault();

      if (currentFolder?.id === myFolder.id) {
        //No need to set menu since we only have 1 option (User Pin)
        handleUserPin(prompt, prompt.pinned || false);
      } else if (setPinOptionsAnchorEl) {
        // Show folder pin options menu if inside a folder
        setPinOptionsAnchorEl(e.currentTarget);
      }

      setPinOptionsMenuOpen((prev) => !prev);
    },
    [currentFolder, handleUserPin, setPinOptionsAnchorEl]
  );

  const handlePinMenuClose = useCallback(() => {
    setPinOptionsAnchorEl(null);
    setPinOptionsMenuOpen(false);
  }, []);

  // Function to determine tooltip text based on pin status
  const getPinTooltipText = useCallback(
    (isPinned: boolean, isFolderPinned: boolean) => {
      if (currentFolder?.id === myFolder.id) {
        return isPinned ? 'Pinned for me' : 'Pin for Me';
      }

      if (isPinned && isFolderPinned) {
        return 'Pinned to Folder & Me';
      }

      if (isPinned) {
        return 'Pin for Folder';
      }

      if (isFolderPinned) {
        return 'Pin for Me';
      }

      return 'Pin for Folder & Me';
    },
    [currentFolder?.id]
  );

  return {
    handleFolderPin,
    handleUserPin,
    handlePinMenuSetup,
    handlePinMenuClose,
    getPinTooltipText,
    pinOptionsAnchorEl,
    pinOptionsMenuOpen,
  };
}
